import React, { useEffect, useState } from 'react';
import { useApi } from '../../../context/ApiProvider';
import { Field, Form, Formik } from "formik";
import moment from 'moment';
import { Link } from 'react-router-dom';
import ActivityIndicator from '../../Helpers/ActivityIndicator';
import { moduleName } from "../../Helpers/ModuleName";
import SearchClients from '../../SearchClients';
import { addImplementationFee, calculateEstimatedValue, calculatePartnerProvision, sumAllModules, normalizeClientBillingSettings } from '../invoiceCalculations';
import BaseColors from '../../../style/BaseColors';

function CreateInvoices() {
    const [clients, setClients] = useState([]);
    const [discrepancies, setDiscrepancies] = useState([]);
    const [members, setMembers] = useState([]);
    const [invoiceDate, setInvoiceDate] = useState(moment().format('YYYY-MM-DD'));
    const [invoices, setInvoices] = useState([]);
    const [searchFilter, setSearchFilter] = useState("");
    const [messages, setMessages] = useState({ success: "", error: "" });
    const [isLoading, setIsLoading] = useState(true);
    const Api = useApi();


    useEffect(() => {
        refresh();
    }, []);

    async function refresh() {
        setIsLoading(true);
        try {
            const [clients, discrepancies, members, invoices] = await Promise.all([
                fetchClients(),
                fetchDiscrepancies(),
                fetchMembers(),
                fetchAllInvoices(),
            ]);
            setClients(clients);
            setDiscrepancies(discrepancies);
            setMembers(members);
            setInvoices(invoices);
            //setMessages({ success: "Data uppdaterad", error: "" });
        } catch (e) {
            setMessages({ success: "", error: "Kunde inte hämta data" });
        } finally {
            setIsLoading(false);
        }
    }

    function fetchClients() {
        //Hämta alla klienter, filtrera ut betalande och aktiva
        return Api.get("/rolf/clients").then((data) => {
            return data.filter((client) => client.active && client.paying);
        });
    }

    function fetchDiscrepancies() { //Hämta faktureringsavvikelser
        return Api.get(`/rolf/invoicediscrepancies?timestamp=${moment().unix()}`);
    }

    function fetchMembers() {
        //Hämta alla medlemmar
        return Api.get("/rolf/members");
    }

    function fetchAllInvoices() {
        if (!invoiceDate) return Promise.resolve([]);

        return Api.get(`/rolf/invoices?latest=true`);
    }

    const targetInvoiceMoment = invoiceDate ? moment(invoiceDate, 'YYYY-MM-DD') : moment();

    return (
        <div>
            <h4>Create invoices</h4>

            <p className="spaceBetween">
                <span className='meta'>Note: Estimated values does <strong>not</strong> take discrepancies into account</span>
            </p>

            <SearchClients searchFilter={searchFilter} setSearchFilter={setSearchFilter} />

            {messages.success && <p className="formControlSuccess">{messages.success}</p>}
            {messages.error && <p className="formControlError">{messages.error}</p>}

            <table>
                <thead>
                    <tr>
                        <th>Client name</th>
                        <th>Partner</th>
                        <th>Partner commission</th>
                        <th>Modules</th>
                        <th>Module&nbsp;price</th>
                        <th>Current discrepancies</th>
                        <th className='spaceBetween'>Settings <input type='date' value={invoiceDate} onChange={(e) => setInvoiceDate(e.target.value)} /></th>
                    </tr>
                </thead>
                <tbody>
                    {
                        invoices && clients && members && clients.filter(client => !invoices.some((invoice) => invoice.clientId == client.ID && moment.unix(invoice.invoiceDate).isSame(targetInvoiceMoment, 'month'))) /* Dölj om faktura redan skapad för vald månad */
                            .filter((client) => client.name.toLowerCase().includes(searchFilter.toLowerCase()) ? true : false) /* Sökfältet */
                            .filter((client) => { /* Dölj om faktura finns skapad inom intervallet */
                                const billingInterval = normalizeClientBillingSettings(client).billingInterval;

                                // Hitta fakturor för klienten inom intervallet
                                const intervalStart = targetInvoiceMoment.clone().subtract(Math.max(billingInterval - 1, 0), 'months').startOf('month');
                                const intervalEnd = targetInvoiceMoment.clone().endOf('month');
                                const clientInvoicesWithinInterval = invoices.filter(invoice =>
                                    invoice.clientId == client.ID &&
                                    moment.unix(invoice.invoiceDate).isBetween(intervalStart, intervalEnd, null, '[]')
                                );
                                // Dölj klienten om det finns faktura inom intervallet
                                return clientInvoicesWithinInterval.length === 0;
                            })
                            .map((client) => {

                                return (
                                    <InvoiceRow
                                        key={client.ID}
                                        client={client}
                                        members={members}
                                        refresh={refresh}
                                        invoices={invoices}
                                        invoiceDate={invoiceDate}
                                        discrepancies={discrepancies}
                                        setMessages={setMessages}
                                    />
                                )
                            })
                    }
                </tbody>
            </table>

            {messages.success && <p className="formControlSuccess">{messages.success}</p>}
            {messages.error && <p className="formControlError">{messages.error}</p>}
        </div>
    );
}

const InvoiceRow = (props) => {
    const { members, refresh, invoiceDate, setMessages, discrepancies, invoices } = props;
    const [hasBeenCreated, setHasBeenCreated] = useState(false);
    const [client, setClient] = useState(props.client);
    const [clientModules, setClientModules] = useState([]);
    const Api = useApi();
    const [modulePrice, setModulePrice] = useState(false);
    const [modulePrices, setModulePrices] = useState({});
    const [priceModel, setPriceModel] = useState(false);
    const [minimumLicenses, setMinimumLicenses] = useState(false);
    const [implementationFee, setImplementationFee] = useState(false);
    const [estimatedValue, setEstimatedValue] = useState(false);
    const [partnerProvision, setPartnerProvision] = useState(false);
    const [partnerProvisionPercentage, setPartnerProvisionPercentage] = useState(false);
    const [billingInterval, setBillingInterval] = useState(1);

    const currentDiscrepancies = discrepancies.filter((discrepancy) => {
        return discrepancy.clientId === client.ID ? true : false;
    })



    useEffect(() => {
        if (!client || !members.length > 0) return;

        const billing = normalizeClientBillingSettings(client);
        setBillingInterval(billing.billingInterval);

        setClientModules(
            client.modules.split(",").map((module) => {
                // Räkna antalet medlemmar med denna modul
                const count = members.filter((member) =>
                    member.modules.some((m) => m.module === module && parseInt(m.clientId) === parseInt(client.ID))
                ).length;

                // Multiplicera med intervallet
                return {
                    name: module,
                    count: count * billing.billingInterval
                };
            })
        );
    }, [client, members, billingInterval]);

    useEffect(() => {
        if (!client || !members || !invoices || !clientModules) return;

        const billing = normalizeClientBillingSettings(client);

        setModulePrice(billing.modulePrice);
        setModulePrices(billing.modulePrices);
        setPriceModel(billing.priceModel);
        setPartnerProvisionPercentage(billing.partnerProvision);
        setMinimumLicenses(billing.minimumLicenses);
        setImplementationFee(addImplementationFee(client, invoices)); //returnerar {sum: X, commission: Y} eller false

        const eV = calculateEstimatedValue(client, invoices, members, clientModules, billingInterval);
        const pP = calculatePartnerProvision(client, invoices, members, clientModules, billingInterval);

        setEstimatedValue(eV);
        setPartnerProvision(pP);

    }, [client, members, invoices, clientModules]);


    return (
        <tr key={client.ID}>
            <td><Link to={`/rolf/clients/${client.ID}`}>{client.name}</Link></td>
            <td>{client.partner ? client.partner : partnerProvisionPercentage > 0 ? <span className='warning'>Partner missing!</span> : ""}</td>
            <td>{partnerProvisionPercentage || 0}%<br /><span className="meta">{partnerProvision || 0} sek</span></td>
            <td>
                {priceModel === "old" ? <>
                    <p className='warning'> Old price model</p>

                    <table>
                        <thead>
                            <tr>
                                {client.modules.split(",").map((module, index) =>
                                    <th key={index}>{moduleName[module]}</th>)
                                }
                                <th>TOTAL:</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                {client.modules.split(",").map((module, index) =>
                                    <td key={index}>{members.filter(member => member.modules.some((m) => m.module == module && m.clientId == client.ID)).length}</td>)
                                }
                                <td><strong>{sumAllModules(client, members) || 0}</strong></td>
                            </tr>
                        </tbody>
                    </table>

                </> : // Nya prissättningen
                    <table>
                        <thead>
                            <tr>
                                {clientModules.length > 0 ? clientModules.map((module, index) =>
                                    <th key={index}>{moduleName[module.name]}</th>) : null
                                }
                                <th>TOTAL:</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                {clientModules.length > 0 ? clientModules.map((module, index) => {
                                    return (
                                        <td key={index}>
                                            <input type="number" value={module.count} onChange={(e) => {
                                                //if(!e.target.value) return;
                                                const newModules = [...clientModules];
                                                newModules[index].count = e.target.value < 0 ? 0 : parseInt(e.target.value);
                                                setClientModules(newModules);
                                            }} style={{ width: 70, border: "1px solid rgba(0,0,0,0.3)" }} />
                                        </td>
                                    )
                                }) : null
                                }
                                <td>
                                    <strong>{sumAllModules(client, members, clientModules) || 0}</strong>
                                </td>
                            </tr>
                        </tbody>

                    </table>
                }
                {minimumLicenses ? <span className="meta">Minimum licenses: {minimumLicenses} | </span> : ""}
                {priceModel === "old" ? <div className="flex flexCenter"><label className="label">Members: </label> <input type="number" value={client.members} onChange={(e) => setClient((prev) => {
                    return {
                        ...prev,
                        members: e.target.value
                    }
                })} style={{ border: "1px solid rgba(0,0,0,0.3)" }} /></div> : <span className="meta">Members: {client.members}</span>}
                {/* OM billingIntervall är satt & över 1, skriv ut vad som används */
                    billingInterval > 1 ? <span className="meta"> | Billing interval: {billingInterval} months</span> : ""
                }
            </td>
            <td>

                <span>REF {modulePrices.reflection || modulePrice}</span><br />
                <span>DEV {modulePrices.development || modulePrice}</span><br />
                <span>ROL {modulePrices.rolfing || modulePrice}</span><br />
                <span>ACT {modulePrices.actionplan || modulePrice}</span><br />
                <span>PUL {modulePrices.pulse || modulePrice}</span>

            </td>
            <td>{currentDiscrepancies.map((disc, index) => (
                <p key={index}><strong key={disc.ID}>{disc.content}</strong></p>
            ))}</td>


            <td className='center'>
                <Formik
                    initialValues={{
                        clientId: client.ID, invoiceDate: invoiceDate, invoiceData: {
                            value: (((estimatedValue && typeof estimatedValue === "object" && Object.prototype.hasOwnProperty.call(estimatedValue, "sum")) ? estimatedValue.sum : estimatedValue) || 0),
                            commission: partnerProvision, invoicePaid: false,
                            commissionInvoiced: false,
                            commissionPaid: false,
                            outstandingCommission: 0,
                            customerName: client.name,
                            partnerId: client.partnerId,
                            partnerName: client.partner,
                            commissionPercentage: (partnerProvisionPercentage || 0),
                            invoiceMetadata: {
                                modulePrices: {
                                    reflection: modulePrices.reflection || modulePrice,
                                    development: modulePrices.development || modulePrice,
                                    rolfing: modulePrices.rolfing || modulePrice,
                                    actionplan: modulePrices.actionplan || modulePrice,
                                    pulse: modulePrices.pulse || modulePrice,
                                },
                                priceModel: priceModel,
                                implementationFee: implementationFee ? implementationFee.sum : 0,
                                implementationFeeCommission: implementationFee ? implementationFee.commission : 0,
                                clientModules: clientModules || [],
                                minimumLicenses: minimumLicenses || 0,
                                nrMembers: client.members,
                                totalModules: priceModel === "old" ? sumAllModules(client, members) : sumAllModules(client, members, clientModules) || 0,
                                billingInterval: client.settings.find(setting => setting.setting === "billingInterval") ? parseInt(client.settings.find(setting => setting.setting === "billingInterval").value.replace(/"/g, "")) : 1
                            }
                        }
                    }}
                    enableReinitialize={true}
                    onSubmit={async (values, { setSubmitting }) => {
                        setSubmitting(true);
                        const invoiceDateUnix = moment(values.invoiceDate).unix();

                        try {
                            await Api.post('/rolf/invoices', { ...values, invoiceDate: invoiceDateUnix });
                            setMessages({ success: "Invoice created successfully", error: "" });
                            setHasBeenCreated(true);
                            setTimeout(() => {
                                refresh(); //Vänta 800 ms innan refresh, visuellt visa grön knapp att faktura skapad
                            }, 800);
                        } catch (error) {
                            setMessages({ success: "", error: "Error creating invoice: " + (error.error || error.message || 'Unknown error') });
                        } finally {
                            setSubmitting(false);
                            setTimeout(() => {
                                setMessages({ success: "", error: "" });
                            }, 5000);
                        }
                    }}>
                    {({ isSubmitting }) => (
                        <Form>
                            <table>
                                <thead>
                                    <tr>
                                        <th>Estimated value</th>
                                        <th>Invoice date</th>
                                        <th>Create invoice</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr>
                                        <td>{((estimatedValue && typeof estimatedValue === "object" && Object.prototype.hasOwnProperty.call(estimatedValue, "sum")) ? (estimatedValue.total || 0) : (estimatedValue || 0))}</td>
                                        <td><Field type='date' name='invoiceDate' className="formControl" /></td>
                                        <td><button type='submit' disabled={isSubmitting} style={{ backgroundColor: hasBeenCreated ? BaseColors.rolfGreen : "" }}>{hasBeenCreated ? "Invoice created" : "Create invoice"} {isSubmitting && <ActivityIndicator color="#ffffff" />}</button></td>
                                    </tr>
                                </tbody>
                            </table>
                        </Form>
                    )
                    }
                </Formik>
                {implementationFee ? <span className="warning">Est. value includes iplementation fee of: {implementationFee.sum} SEK . <br />Partner provision on implementation fee: {implementationFee.commission} SEK.</span> : ""}
            </td>

        </tr>
    )
}

export default CreateInvoices;